import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  Package,
  RefreshCw,
  Trash2,
  X,
  XCircle,
} from 'lucide-react';
import clsx from 'clsx';

import { CopyButton } from '@/components/CopyButton';
import {
  forceDowngradeRetry,
  getTaskFile,
  retryTask,
  uninstallAndRetry,
} from '@/lib/installRunner';
import {
  useInstallTasks,
  type InstallTask,
  type InstallTaskStage,
} from '@/store/installTasks';

/**
 * Bottom-right floating queue of install tasks.
 *
 * Simplified layout (after user feedback):
 *   - 0 visible tasks  → nothing renders
 *   - 1 visible task   → just one card (no summary chip on top)
 *   - 2+ tasks        → optional summary chip showing aggregate counts
 *
 * Per-stage palette (high-contrast so a glance tells the user state):
 *   queued / uploading / installing → accent (sky / cyan)
 *   success                         → emerald
 *   conflict                        → amber
 *   failed                          → rose
 *
 * Long file names wrap rather than truncate, with a CopyButton inline
 * so the user can grab the full file name (and any error message)
 * without having to right-click the OS-level toast.
 */

const HIDE_SUCCESS_AFTER_MS = 8_000;
const DOCK_WIDTH = 'w-[380px] max-w-[calc(100vw-2rem)]';

export function InstallTasksDock(): JSX.Element | null {
  const tasks = useInstallTasks((s) => s.tasks);
  const dismiss = useInstallTasks((s) => s.dismiss);
  const dismissAllFinished = useInstallTasks((s) => s.dismissAllFinished);

  const visible = useMemo<InstallTask[]>(() => {
    const now = Date.now();
    return tasks.filter((t) => {
      if (t.dismissed) return false;
      if (
        t.stage === 'success' &&
        t.finishedAtMs &&
        now - t.finishedAtMs > HIDE_SUCCESS_AFTER_MS
      )
        return false;
      return true;
    });
  }, [tasks]);

  // Tick once a second so success cards age out without extra store
  // mutations. Off when there's nothing transient to age.
  useTicker(visible.some((t) => t.stage === 'success'));

  if (visible.length === 0) return null;

  const active = visible.filter((t) =>
    (['queued', 'uploading', 'installing'] as InstallTaskStage[]).includes(t.stage)
  ).length;
  const conflicts = visible.filter((t) => t.stage === 'conflict').length;
  const failures = visible.filter((t) => t.stage === 'failed').length;
  const showSummary = visible.length > 1;

  return (
    <div
      className={clsx(
        'pointer-events-auto fixed bottom-4 right-4 z-[55] flex flex-col gap-2',
        DOCK_WIDTH
      )}
      role="status"
      aria-live="polite"
    >
      {showSummary && (
        <div className="flex items-center gap-2 rounded-md border border-line bg-bg-card px-3 py-1.5 text-[12px] shadow-card">
          <Package className="h-3.5 w-3.5 text-accent" />
          <span className="font-medium text-ink">
            {visible.length} 个安装任务
          </span>
          <span className="text-ink-dim">
            {[
              active > 0 ? `${active} 进行中` : null,
              conflicts > 0 ? `${conflicts} 冲突` : null,
              failures > 0 ? `${failures} 失败` : null,
            ]
              .filter(Boolean)
              .join(' · ') || '全部完成'}
          </span>
          <button
            type="button"
            className="ml-auto btn-ghost btn-xs"
            onClick={dismissAllFinished}
            title="清除已完成"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto pr-1">
        {visible.map((t) => (
          <TaskCard key={t.id} task={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </div>
  );
}

/* ---------- card ---------- */

function TaskCard({
  task,
  onDismiss,
}: {
  task: InstallTask;
  onDismiss: () => void;
}): JSX.Element {
  // Auto-open details for non-success terminal states so the user
  // sees the failure reason + action buttons without an extra click.
  const [open, setOpen] = useState(
    task.stage === 'conflict' || task.stage === 'failed'
  );

  const palette = stagePalette(task.stage);

  return (
    <div
      className={clsx(
        'rounded-md border shadow-card transition',
        palette.border,
        palette.bg
      )}
    >
      <div className="flex items-start gap-2 px-3 py-2">
        <StageIcon stage={task.stage} className={palette.icon} />

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5">
            <span
              className={clsx(
                'flex-1 break-all text-[12px] font-medium leading-snug',
                palette.title
              )}
              title={task.fileName}
            >
              {task.fileName}
            </span>
            <span
              className={clsx(
                'mt-0.5 shrink-0 rounded px-1 text-[10px] uppercase',
                palette.tag
              )}
            >
              {task.ext}
            </span>
            <CopyButton
              text={task.fileName}
              label="复制文件名"
              size="sm"
              className="mt-0.5"
            />
          </div>
          <div
            className={clsx(
              'mt-0.5 text-[11px] leading-snug',
              palette.subtitle
            )}
          >
            {task.statusText ?? stageLabel(task.stage)}
            {task.serialLabel ? ` · ${task.serialLabel}` : ''}
          </div>
          {(task.stage === 'uploading' ||
            task.stage === 'installing' ||
            task.stage === 'queued') && (
            <IndeterminateBar tone={palette.tone} />
          )}
        </div>

        <div className="flex shrink-0 items-center gap-0.5">
          {(task.stage === 'conflict' ||
            task.stage === 'failed' ||
            task.stage === 'success') && (
            <button
              type="button"
              className="btn-ghost btn-xs"
              aria-label={open ? '收起' : '展开'}
              onClick={() => setOpen((v) => !v)}
            >
              {open ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
          )}
          <button
            type="button"
            className="btn-ghost btn-xs"
            onClick={onDismiss}
            aria-label="关闭"
            title="移除卡片"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {open &&
        (task.stage === 'conflict' ||
          task.stage === 'failed' ||
          task.stage === 'success') && (
          <div
            className={clsx(
              'border-t px-3 py-2 text-[11px]',
              palette.divider,
              palette.subtitle
            )}
          >
            {task.stage === 'conflict' && task.conflict && (
              <ConflictDetails task={task} />
            )}
            {task.stage === 'failed' && <FailedDetails task={task} />}
            {task.stage === 'success' && task.result && (
              <SuccessDetails task={task} />
            )}
          </div>
        )}
    </div>
  );
}

/* ---------- per-stage detail bodies ---------- */

function ConflictDetails({ task }: { task: InstallTask }): JSX.Element {
  const c = task.conflict!;
  const hasFile = getTaskFile(task.id) !== null;
  const canUninstall =
    (c.kind === 'signature_mismatch' || c.kind === 'version_downgrade') &&
    Boolean(c.packageName);
  const canForceDowngrade = c.kind === 'version_downgrade' || c.kind === 'unknown';
  const canPlainRetry = c.kind === 'already_exists' || c.kind === 'unknown';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-ink">
        <span className="font-medium">{kindLabel(c.kind)}</span>
        {c.packageName && (
          <>
            <span className="font-mono">· {c.packageName}</span>
            <CopyButton text={c.packageName} label="复制包名" size="sm" />
          </>
        )}
        {c.token && (
          <span className="ml-1 text-[10px] opacity-70">({c.token})</span>
        )}
      </div>
      <ErrorBlock text={c.rawLine} />
      <div className="flex flex-wrap gap-2 pt-1">
        {canUninstall && (
          <button
            type="button"
            className="btn-ghost btn-xs border border-line bg-bg-card"
            onClick={() => void uninstallAndRetry(task)}
            disabled={!hasFile}
            title={
              hasFile
                ? `卸载 ${c.packageName} 后重新安装`
                : '原始文件已被浏览器释放，请重新拖入'
            }
          >
            <Trash2 className="h-3 w-3" /> 卸载并重装
          </button>
        )}
        {canForceDowngrade && (
          <button
            type="button"
            className="btn-ghost btn-xs border border-line bg-bg-card"
            onClick={() => forceDowngradeRetry(task)}
            disabled={!hasFile}
            title={
              hasFile
                ? '使用 -d 强制覆盖（仅适用于 debuggable / 测试 APK）'
                : '原始文件已被浏览器释放，请重新拖入'
            }
          >
            <RefreshCw className="h-3 w-3" /> 强制 -d 降级
          </button>
        )}
        {canPlainRetry && (
          <button
            type="button"
            className="btn-ghost btn-xs border border-line bg-bg-card"
            onClick={() => retryTask(task)}
            disabled={!hasFile}
          >
            <RefreshCw className="h-3 w-3" /> 重试
          </button>
        )}
        {!hasFile && (
          <span className="self-center text-[10.5px] text-ink-dim">
            （文件已被浏览器释放，请重新拖入）
          </span>
        )}
      </div>
    </div>
  );
}

function FailedDetails({ task }: { task: InstallTask }): JSX.Element {
  const hasFile = getTaskFile(task.id) !== null;
  return (
    <div className="flex flex-col gap-2">
      <ErrorBlock text={task.error ?? '未知错误'} />
      {hasFile ? (
        <div>
          <button
            type="button"
            className="btn-ghost btn-xs border border-line bg-bg-card"
            onClick={() => retryTask(task)}
          >
            <RefreshCw className="h-3 w-3" /> 重试
          </button>
        </div>
      ) : (
        <span className="text-[10.5px] text-ink-dim">
          （文件已被浏览器释放，请重新拖入）
        </span>
      )}
    </div>
  );
}

function SuccessDetails({ task }: { task: InstallTask }): JSX.Element {
  const r = task.result!;
  return (
    <div className="flex flex-col gap-1 text-ink">
      {r.package && (
        <div className="flex items-center gap-1.5">
          包名 <span className="font-mono">{r.package}</span>
          <CopyButton text={r.package} label="复制包名" size="sm" />
        </div>
      )}
      {r.versionCode !== undefined && <div>versionCode = {r.versionCode}</div>}
      {r.splits && r.splits.length > 0 && (
        <div>{r.splits.length} 个 split 已上传</div>
      )}
      {r.obb && r.obb.length > 0 && (
        <div>
          {r.obb.filter((o) => o.pushed).length}/{r.obb.length} 个 OBB 推送成功
        </div>
      )}
      {r.durationMs !== undefined && (
        <div>耗时 {(r.durationMs / 1000).toFixed(1)} s</div>
      )}
    </div>
  );
}

/**
 * Multi-line, monospace error block with a copy button in the
 * corner. Used by both failed + conflict cards so the user can
 * grab the raw `Failure [...]` line into a bug report.
 */
function ErrorBlock({ text }: { text: string }): JSX.Element {
  return (
    <div className="relative rounded bg-bg-soft px-2 py-1.5 pr-7 font-mono text-[10.5px] leading-snug text-ink">
      <span className="block whitespace-pre-wrap break-all">{text}</span>
      <CopyButton
        text={text}
        label="复制错误信息"
        size="sm"
        className="absolute right-1 top-1"
      />
    </div>
  );
}

/* ---------- palette + helpers ---------- */

interface StagePalette {
  border: string;
  bg: string;
  icon: string;
  title: string;
  subtitle: string;
  divider: string;
  tag: string;
  tone: 'accent' | 'emerald' | 'amber' | 'rose';
}

function stagePalette(stage: InstallTaskStage): StagePalette {
  switch (stage) {
    case 'success':
      return {
        border: 'border-emerald-500/60',
        bg: 'bg-emerald-500/[0.08]',
        icon: 'text-emerald-500',
        title: 'text-ink',
        subtitle: 'text-ink-muted',
        divider: 'border-emerald-500/30',
        tag: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
        tone: 'emerald',
      };
    case 'conflict':
      return {
        border: 'border-amber-500/70',
        bg: 'bg-amber-500/[0.10]',
        icon: 'text-amber-500',
        title: 'text-ink',
        subtitle: 'text-ink-muted',
        divider: 'border-amber-500/30',
        tag: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
        tone: 'amber',
      };
    case 'failed':
      return {
        border: 'border-rose-500/70',
        bg: 'bg-rose-500/[0.10]',
        icon: 'text-rose-500',
        title: 'text-ink',
        subtitle: 'text-ink-muted',
        divider: 'border-rose-500/30',
        tag: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
        tone: 'rose',
      };
    default:
      // queued / uploading / installing
      return {
        border: 'border-accent/60',
        bg: 'bg-accent/[0.06]',
        icon: 'text-accent',
        title: 'text-ink',
        subtitle: 'text-ink-muted',
        divider: 'border-accent/30',
        tag: 'bg-accent/15 text-accent',
        tone: 'accent',
      };
  }
}

function StageIcon({
  stage,
  className,
}: {
  stage: InstallTaskStage;
  className?: string;
}): JSX.Element {
  const base = clsx('mt-0.5 h-4 w-4 shrink-0', className);
  switch (stage) {
    case 'queued':
    case 'uploading':
    case 'installing':
      return <Loader2 className={clsx(base, 'animate-spin')} />;
    case 'success':
      return <CheckCircle2 className={base} />;
    case 'conflict':
      return <AlertTriangle className={base} />;
    case 'failed':
      return <XCircle className={base} />;
  }
}

function stageLabel(stage: InstallTaskStage): string {
  switch (stage) {
    case 'queued':
      return '排队中';
    case 'uploading':
      return '上传中';
    case 'installing':
      return '安装中';
    case 'success':
      return '安装成功';
    case 'conflict':
      return '需要处理冲突';
    case 'failed':
      return '安装失败';
  }
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'version_downgrade':
      return '版本降级被拒绝';
    case 'signature_mismatch':
      return '签名不一致';
    case 'sdk_mismatch':
      return 'SDK 不兼容';
    case 'already_exists':
      return '应用已存在';
    default:
      return '安装冲突';
  }
}

function IndeterminateBar({
  tone,
}: {
  tone: 'accent' | 'emerald' | 'amber' | 'rose';
}): JSX.Element {
  const fill =
    tone === 'emerald'
      ? 'bg-emerald-500'
      : tone === 'amber'
      ? 'bg-amber-500'
      : tone === 'rose'
      ? 'bg-rose-500'
      : 'bg-accent';
  return (
    <div className="mt-1 h-0.5 overflow-hidden rounded bg-bg-soft">
      <div
        className={clsx(
          'h-full w-1/3 animate-[indeterminate_1.4s_linear_infinite]',
          fill
        )}
      />
    </div>
  );
}

function useTicker(active: boolean): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 1_000);
    return () => window.clearInterval(id);
  }, [active]);
}
